import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/marketing/SEO";

const Fribourg = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Piktable Fribourg",
    "description": "Gestion de réservations pour restaurants à Fribourg",
    "url": "https://piktable.ch/fribourg",
    "areaServed": {
      "@type": "City",
      "name": "Fribourg",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Canton de Fribourg"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "46.8067",
      "longitude": "7.1620"
    },
    "priceRange": "CHF 10-99",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Fribourg",
      "addressCountry": "CH"
    },
    "email": "contact@piktable.ch",
    "sameAs": ["https://www.instagram.com/piktable"],
    "image": "https://piktable.ch/screenshots/dashboard-overview.png"
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://piktable.ch/" },
      { "@type": "ListItem", "position": 2, "name": "Fribourg", "item": "https://piktable.ch/fribourg" }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Gestion de réservations pour restaurants à Fribourg"
        description="Piktable aide les restaurants fribourgeois à gérer leurs réservations. Solution suisse romande simple et abordable : 49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs. Zéro commission par couvert."
        canonical="/fribourg"
        structuredData={[structuredData, breadcrumbData]}
      />
      <Header />
      <main className="pt-16">
        <section className="py-24 bg-gradient-hero">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="font-playfair text-5xl md:text-6xl font-bold text-foreground">
                Gestion de réservations pour restaurants à Fribourg
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Piktable aide les restaurants, cafés et bistrots fribourgeois à gérer leurs réservations efficacement. Solution suisse romande, simple et abordable.
              </p>
              <div className="flex justify-center pt-6">
                <Button size="lg" onClick={() => navigate("/signup")}>
                  Essayer gratuitement
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="text-center">
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Pourquoi Piktable à Fribourg ?
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🎯</div>
                  <h3 className="text-2xl font-bold text-foreground">Simple et efficace</h3>
                  <p className="text-muted-foreground">
                    Interface pensée pour les petites équipes. Gérez vos réservations, vos tables et vos clients en quelques clics.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">💰</div>
                  <h3 className="text-2xl font-bold text-foreground">Prix transparents</h3>
                  <p className="text-muted-foreground">
                    49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs du Printemps 2026. Zéro commission par couvert, tarifs clairs en CHF, sans surprise.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">📧</div>
                  <h3 className="text-2xl font-bold text-foreground">E-mails automatiques</h3>
                  <p className="text-muted-foreground">
                    Confirmations, rappels et modifications envoyés automatiquement à vos clients. Gain de temps assuré.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🎁</div>
                  <h3 className="text-2xl font-bold text-foreground">Essai gratuit</h3>
                  <p className="text-muted-foreground">
                    Un mois gratuit pour tester Piktable dans votre restaurant. Sans engagement, sans carte bancaire.
                  </p>
                </div>
              </div>

              <div className="mt-12 space-y-4">
                <h3 className="font-playfair text-2xl font-bold text-foreground">
                  Fribourg : une ville bilingue au carrefour de la Suisse romande
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Fribourg se distingue par son bilinguisme français-allemand et sa position au carrefour de la Suisse romande et alémanique. Les restaurateurs fribourgeois accueillent une clientèle diversifiée — étudiants de l'Université, familles de la région et visiteurs attirés par la Vieille-Ville médiévale. De Pérolles à Bourguillon, en passant par le quartier de l'Auge, chaque établissement a ses propres horaires et sa propre dynamique. Piktable s'adapte à cette réalité avec une interface entièrement en français, des créneaux horaires flexibles et une gestion de tables pensée pour les petits établissements comme pour les restaurants plus grands.
                </p>
              </div>

              <div className="mt-16 p-8 rounded-2xl bg-primary/5 border-2 border-primary/20">
                <h3 className="font-playfair text-3xl font-bold text-foreground mb-4 text-center">
                  Adapté aux établissements fribourgeois
                </h3>
                <p className="text-lg text-muted-foreground text-center mb-8">
                  Que vous soyez en Vieille-Ville, à Pérolles ou dans le quartier de l'Auge, Piktable s'adapte à votre restaurant.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                    Commencer gratuitement
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full sm:w-auto"
                  >
                    En savoir plus
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Fribourg;
